import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { BottomNavBar } from '../../components/BottomNavBar';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { ChecklistCardScroll } from '../../components/ChecklistCardScroll';
import { FINAL_CARE_TASKS_CONFIG, PERSONAL_TASKS_CONFIG, MEDICAL_TASKS_CONFIG, PASSWORDS_TASKS_CONFIG, CONTACTS_TASKS_CONFIG, EXPENSES_TASKS_CONFIG, FINANCE_TASKS_CONFIG, PROPERTY_TASKS_CONFIG, FINAL_WISHES_TASKS_CONFIG, HEALTHCARE_DIRECTIVES_TASKS_CONFIG, ESTATE_PLANNING_TASKS_CONFIG, CARE_PREFERENCES_TASKS_CONFIG, LOYALTY_POINTS_TASKS_CONFIG } from '../../constants/taskIds';
import { TaskItem } from '../../components/TaskItem';
import { RootStackParamList } from '../../types/navigation';
import { vaultService } from '../../services/vaultService';
import { contactService } from '../../services/contactService';
import { expenseService } from '../../services/expenseService';
import { loyaltyPointService } from '../../services/loyaltyPointService';
import { documentStorageService } from '../../services/documentStorageService';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { guardVaultEdit } from '../../utils/vaultEditGate';
import { UpgradeBanner } from '../../components/UpgradeBanner';

import { createLogger } from '../../utils/logger';
const logger = createLogger('FinalCareChecklist');

export const FinalCareChecklistScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const { selectedUserId, isViewingOthersData, availablePrimaryUsers } = useUserSelection();
  const { canViewOwnData, canEditData } = useSubscription();

  // Get the name of the user whose data we're viewing
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;
  const [completedTasks, setCompletedTasks] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('cloud');
  const [categoryProgress, setCategoryProgress] = useState<any[]>([]);
  const [completedPasswordTasks, setCompletedPasswordTasks] = useState<string[]>([]);
  const [completedContactTasks, setCompletedContactTasks] = useState<string[]>([]);
  const [completedExpenseTasks, setCompletedExpenseTasks] = useState<string[]>([]);
  const [completedMedicalTasks, setCompletedMedicalTasks] = useState<string[]>([]);
  const [completedFinanceTasks, setCompletedFinanceTasks] = useState<string[]>([]);
  const [completedPropertyTasks, setCompletedPropertyTasks] = useState<string[]>([]);
  const [completedFinalWishesTasks, setCompletedFinalWishesTasks] = useState<string[]>([]);
  const [completedHealthcareDirectivesTasks, setCompletedHealthcareDirectivesTasks] = useState<string[]>([]);
  const [completedEstatePlanningTasks, setCompletedEstatePlanningTasks] = useState<string[]>([]);
  const [completedCarePreferencesTasks, setCompletedCarePreferencesTasks] = useState<string[]>([]);
  const [completedLoyaltyPointTasks, setCompletedLoyaltyPointTasks] = useState<string[]>([]);
  const [completedDocumentTasks, setCompletedDocumentTasks] = useState<string[]>([]);
  const cacheRef = useRef<{ completed: number[]; timestamp: number } | null>(null);
  const CACHE_DURATION = 60000; // 60 seconds

  const fetchCompletedTasks = async (useCache = true) => {
    const now = Date.now();
    if (useCache && cacheRef.current && (now - cacheRef.current.timestamp < CACHE_DURATION)) {
      setCompletedTasks(cacheRef.current.completed);
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const token = await getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Completed tasks fetch timeout')), 3000);
      });

      // Build URL with optional targetUserId for viewing other user's data
      const url = isViewingOthersData && selectedUserId
        ? `${API_URL}/api/tasks/user/completed-tasks?targetUserId=${selectedUserId}`
        : `${API_URL}/api/tasks/user/completed-tasks`;

      const fetchPromise = fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const completedRes = await Promise.race([fetchPromise, timeoutPromise]);
      let completed: number[] = [];
      if (completedRes.ok) {
        const completedData = await completedRes.json();
        logger.debug('API /user/completed-tasks response:', completedData, isViewingOthersData ? `(viewing ${selectedUserId})` : '');
        completed = (completedData.completedTaskIds || [])
          .map((id: any) => Number(id))
          .filter((id: number) => !isNaN(id));
      }
      // Include locally-declined final care tasks (fallback if backend not updated yet)
      try {
        const declinedAdvanceDirective = await AsyncStorage.getItem('task_declined_56');
        if (declinedAdvanceDirective === 'true' && !completed.includes(56)) {
          completed.push(56);
        }
        const declinedLTC = await AsyncStorage.getItem('task_declined_57');
        if (declinedLTC === 'true' && !completed.includes(57)) {
          completed.push(57);
        }
        const declinedDNR = await AsyncStorage.getItem('task_declined_58');
        if (declinedDNR === 'true' && !completed.includes(58)) {
          completed.push(58);
        }
        const declinedBreathing = await AsyncStorage.getItem('task_declined_59');
        if (declinedBreathing === 'true' && !completed.includes(59)) {
          completed.push(59);
        }
        const declinedFeeding = await AsyncStorage.getItem('task_declined_60');
        if (declinedFeeding === 'true' && !completed.includes(60)) {
          completed.push(60);
        }
        const declinedMedicalPOA = await AsyncStorage.getItem('task_declined_61');
        if (declinedMedicalPOA === 'true' && !completed.includes(61)) {
          completed.push(61);
        }
        const declinedLivingTrust = await AsyncStorage.getItem('task_declined_63');
        if (declinedLivingTrust === 'true' && !completed.includes(63)) {
          completed.push(63);
        }
      } catch {}

      setCompletedTasks(completed);
      cacheRef.current = { completed, timestamp: now };
    } catch (error: any) {
      // Suppress network errors to prevent console spam - this is expected when backend API is not available
      if (error.message?.includes('Network request failed') ||
          error.message?.includes('fetch timeout') ||
          error.message?.includes('Request failed') ||
          error.response?.status === 500 ||
          error.code === 'ECONNREFUSED') {
        logger.debug('Backend API not available (expected in development), using empty completed tasks');
        setCompletedTasks([]);
      } else {
        logger.error('Error fetching completed tasks:', error);
        setCompletedTasks([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshTasks = () => {
    cacheRef.current = null;
    fetchCompletedTasks(false);
  };

  const fetchSubscriptionStatus = async () => {
    // TEMPORARY: Set as paid user for development/testing
    setSubscriptionStatus('cloud');
    /* COMMENTED OUT - Original API logic
    try {
      const token = await getAuthToken();
      if (!token) {
        setSubscriptionStatus('free');
        return;
      }
      const response = await fetch(`${API_URL}/auth/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        setSubscriptionStatus('free');
        return;
      }
      const data = await response.json();
      setSubscriptionStatus(data.tier || 'free');
    } catch (e) {
      setSubscriptionStatus('free');
    }
    */
  };

  const fetchCategoryProgress = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/tasks/category-progress`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCategoryProgress(data);
      } else {
        setCategoryProgress([]);
      }
    } catch (e) {
      setCategoryProgress([]);
    }
  };

  const fetchPasswordCompletion = async () => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Password fetch timeout')), 3000);
      });

      // For password tasks, determine completion based on actual password data
      const fetchPromise = vaultService.getAllPasswords();
      const allPasswords = await Promise.race([fetchPromise, timeoutPromise]);
      const completedPasswordTasks: string[] = [];

      PASSWORDS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryPasswords = allPasswords.filter(p => p.category.toLowerCase() === task.category.toLowerCase());
          if (categoryPasswords.length > 0) {
            completedPasswordTasks.push(task.id.toString());
          }
        }
      });

      setCompletedPasswordTasks(completedPasswordTasks);
    } catch (error: any) {
      // Suppress network errors to prevent console spam - this is expected when Vault API is not available
      if (error.message?.includes('fetch timeout') ||
          error.message?.includes('Request failed') ||
          error.message?.includes('Network request failed') ||
          error.response?.status === 500 ||
          error.code === 'ECONNREFUSED') {
        logger.debug('Vault API not available - using fallback');
        setCompletedPasswordTasks([]);
      } else {
        logger.error('Error fetching completed password tasks:', error);
        setCompletedPasswordTasks([]);
      }
    }
  };

  const fetchContactCompletion = async () => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Contact fetch timeout')), 3000);
      });

      // For contact tasks, determine completion based on actual contact data
      const fetchPromise = contactService.getAllContacts();
      const allContacts = await Promise.race([fetchPromise, timeoutPromise]);
      const completedContactTasks: string[] = [];

      CONTACTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryContacts = allContacts.filter(c => c.category.toLowerCase() === task.category.toLowerCase());
          if (categoryContacts.length > 0) {
            completedContactTasks.push(task.id.toString());
          }
        }
      });

      setCompletedContactTasks(completedContactTasks);
    } catch (error: any) {
      // Suppress network errors to prevent console spam - this is expected when Contact API is not available
      if (error.message?.includes('fetch timeout') ||
          error.message?.includes('Request failed') ||
          error.message?.includes('Network request failed') ||
          error.response?.status === 500 ||
          error.code === 'ECONNREFUSED') {
        logger.debug('Contact API not available - using fallback');
        setCompletedContactTasks([]);
      } else {
        logger.error('Error fetching completed contact tasks:', error);
        setCompletedContactTasks([]);
      }
    }
  };

  const fetchExpenseCompletion = async () => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Expense fetch timeout')), 3000);
      });

      // For expense tasks, determine completion based on actual expense data
      const fetchPromise = expenseService.getAllExpenses();
      const allExpenses = await Promise.race([fetchPromise, timeoutPromise]);
      const completedExpenseTasks: string[] = [];

      EXPENSES_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
          if (categoryExpenses.length > 0) {
            completedExpenseTasks.push(task.id.toString());
          }
        }
      });

      setCompletedExpenseTasks(completedExpenseTasks);
    } catch (error: any) {
      // Suppress network errors to prevent console spam - this is expected when Expense API is not available
      if (error.message?.includes('fetch timeout') ||
          error.message?.includes('Request failed') ||
          error.message?.includes('Network request failed') ||
          error.response?.status === 500 ||
          error.code === 'ECONNREFUSED') {
        logger.debug('Expense API not available - using fallback');
        setCompletedExpenseTasks([]);
      } else {
        logger.error('Error fetching completed expense tasks:', error);
        setCompletedExpenseTasks([]);
      }
    }
  };

  const fetchLoyaltyPointCompletion = async () => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Loyalty point fetch timeout')), 3000);
      });

      // For loyalty point tasks, determine completion based on actual loyalty point data
      const fetchPromise = loyaltyPointService.getAllLoyaltyPoints();
      const allLoyaltyPoints = await Promise.race([fetchPromise, timeoutPromise]);
      const completedLoyaltyPointTasks: string[] = [];

      LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
          if (categoryLoyaltyPoints.length > 0) {
            completedLoyaltyPointTasks.push(task.id.toString());
          }
        }
      });

      setCompletedLoyaltyPointTasks(completedLoyaltyPointTasks);
    } catch (error: any) {
      // Suppress network errors to prevent console spam - this is expected when Loyalty Point API is not available
      if (error.message?.includes('fetch timeout') ||
          error.message?.includes('Request failed') ||
          error.message?.includes('Network request failed') ||
          error.response?.status === 500 ||
          error.code === 'ECONNREFUSED') {
        logger.debug('Loyalty Point API not available - using fallback');
        setCompletedLoyaltyPointTasks([]);
      } else {
        logger.error('Error fetching completed loyalty point tasks:', error);
        setCompletedLoyaltyPointTasks([]);
      }
    }
  };

  const fetchDocumentCompletion = async () => {
    try {
      const allDocuments = await documentStorageService.listDocuments();
      const completedDocumentTasksList: string[] = [];

      const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
      const DOCUMENT_TASK_IDS = ['dm1', 'dm2', 'dm3', 'dm4', 'dm5', 'dm6', 'dm7', 'dm8'];

      DOCUMENT_CATEGORIES.forEach((category, index) => {
        const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
        if (categoryDocuments.length > 0) {
          completedDocumentTasksList.push(DOCUMENT_TASK_IDS[index]);
        }
      });

      setCompletedDocumentTasks(completedDocumentTasksList);
    } catch (error) {
      logger.error('Error fetching completed document tasks:', error);
      setCompletedDocumentTasks([]);
    }
  };


  // Fetch healthcare directives completion status
  const fetchHealthcareDirectivesCompletion = async () => {
    try {
      const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedHealthcareDirectives = completedTasks
        .filter(id => healthcareDirectivesIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedHealthcareDirectivesTasks(completedHealthcareDirectives);
    } catch (error: any) {
      logger.error('Error calculating completed healthcare directives tasks:', error);
      setCompletedHealthcareDirectivesTasks([]);
    }
  };

  // Fetch estate planning completion status
  const fetchEstatePlanningCompletion = async () => {
    try {
      const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
      const completedEstatePlanning = completedTasks
        .filter(id => estatePlanningIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedEstatePlanningTasks(completedEstatePlanning);
    } catch (error: any) {
      logger.error('Error calculating completed estate planning tasks:', error);
      setCompletedEstatePlanningTasks([]);
    }
  };

  // Fetch care preferences completion status
  const fetchCarePreferencesCompletion = async () => {
    try {
      const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCarePreferences = completedTasks
        .filter(id => carePreferencesIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedCarePreferencesTasks(completedCarePreferences);
    } catch (error: any) {
      logger.error('Error calculating completed care preferences tasks:', error);
      setCompletedCarePreferencesTasks([]);
    }
  };

  // Fetch final wishes completion status (using PropertyChecklistScreen pattern)
  const fetchFinalWishesCompletion = async () => {
    try {
      // Mirror PropertyChecklistScreen: compute from completedTasks ∩ FINAL_WISHES_TASKS_CONFIG
      const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinalWishes = completedTasks
        .filter(id => finalWishesIds.has(Number(id)))
        .map(id => id.toString());

      setCompletedFinalWishesTasks(completedFinalWishes);
    } catch (error: any) {
      logger.error('Error calculating completed final wishes tasks:', error);
      setCompletedFinalWishesTasks([]);
    }
  };

  useFocusEffect(
    useCallback(() => {
      refreshTasks();
      fetchPasswordCompletion();
      fetchContactCompletion();
      fetchExpenseCompletion();
      fetchLoyaltyPointCompletion();
      fetchDocumentCompletion();
      // Final wishes, healthcare directives, estate planning, and care preferences completion is derived from completedTasks; no direct service calls
      fetchFinalWishesCompletion();
      fetchHealthcareDirectivesCompletion();
      fetchEstatePlanningCompletion();
      fetchCarePreferencesCompletion();
      return () => {};
    }, [])
  );

  // Trigger a refresh when a caller sends a refreshKey param
  useEffect(() => {
    const refreshKey = (route as any)?.params?.refreshKey;
    if (refreshKey) {
      refreshTasks();
    }
  }, [route]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  // Refetch data when selectedUserId changes (viewing different user's data)
  useEffect(() => {
    if (selectedUserId) {
      logger.debug('FinalCareChecklistScreen: selectedUserId changed, refetching...', selectedUserId);
      cacheRef.current = null; // Clear cache to force refetch
      fetchCompletedTasks(false);
    }
  }, [selectedUserId]);

  // Recalculate final wishes, healthcare directives, estate planning, and care preferences completion when completedTasks changes
  useEffect(() => {
    if (completedTasks.length >= 0) { // Only run when completedTasks has been loaded
      fetchFinalWishesCompletion();
      fetchHealthcareDirectivesCompletion();
      fetchEstatePlanningCompletion();
      fetchCarePreferencesCompletion();
    }
  }, [completedTasks]);

  // Recalculate categories when password, contact, expense, loyalty point, final wishes, healthcare directives, estate planning, and care preferences completion status changes
  useEffect(() => {
    // This will trigger a re-render of the categories with updated completion data
  }, [completedPasswordTasks, completedContactTasks, completedExpenseTasks, completedLoyaltyPointTasks, completedFinalWishesTasks, completedHealthcareDirectivesTasks, completedEstatePlanningTasks, completedCarePreferencesTasks, categoryProgress]);

  // Debug logs for completedTasks and task completion status
  useEffect(() => {
    logger.debug('=== FINAL CARE DEBUG ===');
    logger.debug('completedTasks state updated:', completedTasks);
    logger.debug('FINAL_CARE_TASKS_CONFIG ids:', FINAL_CARE_TASKS_CONFIG.map(t => t.id));
    const finalCareIds = new Set(FINAL_CARE_TASKS_CONFIG.map(t => Number(t.id)));
    const completedFinalCareCount = completedTasks.filter(id => finalCareIds.has(Number(id))).length;
    logger.debug('Completed final care count for card:', completedFinalCareCount);
    logger.debug('Total final care tasks:', FINAL_CARE_TASKS_CONFIG.length);
    FINAL_CARE_TASKS_CONFIG.forEach(task => {
      logger.debug(`Task: ${task.title}, id: ${task.id}, isCompleted: ${completedTasks.includes(Number(task.id))}`);
    });
    logger.debug('=== END FINAL CARE DEBUG ===');
  }, [completedTasks]);

  const renderTaskItem = (task: any) => {
    const taskId = Number(task.id);
    // CRITICAL: Pure intersection-based completion check
    const isCompleted = completedTasks.includes(taskId);
    // Determine if tasks should be disabled (user has no active subscription and not viewing others' data)
    const isTaskDisabled = !canViewOwnData && !isViewingOthersData;
    let showButton = true;
    let buttonText = '';
    let onPress: (() => void) = () => {};
    let buttonColor = '#29BFC0';

    if (subscriptionStatus === 'free') {
      showButton = false;
    } else {
      showButton = true;

      // When viewing another user's data, always show "View Data" and navigate to data screen
      if (isViewingOthersData) {
        if (isCompleted) {
          buttonText = 'View Data';
          buttonColor = '#FF9F5A';
          onPress = () => (navigation as any).navigate('FinalCare', {
            screen: task.dataScreen as any,
            params: { targetUserId: selectedUserId, viewOnly: true }
          });
        } else {
          // Task not completed by primary user - show disabled state
          buttonText = 'Not Completed';
          buttonColor = '#CCCCCC';
          onPress = () => {}; // No action for incomplete tasks when viewing others' data
        }
      } else if (isCompleted) {
        buttonText = 'Review Information';
        buttonColor = '#FF9F5A';
        onPress = () => (navigation as any).navigate('FinalCare', { screen: task.dataScreen as any });
      } else {
        buttonText = 'Click to Complete';
        buttonColor = '#29BFC0';
        onPress = () =>
          guardVaultEdit(canEditData, navigation, () => (navigation as any).navigate('FinalCare', { screen: task.inputScreen as any }));
      }
    }
    return (
      <TaskItem
        key={task.id}
        task={{ id: String(task.id), title: task.title }}
        isCompleted={isCompleted}
        onPress={onPress}
        hideActionButton={!showButton}
        buttonText={buttonText}
        buttonColor={buttonColor}
        subscriptionTier={subscriptionStatus}
        onCheckboxPress={() => {}}
        isDisabled={isTaskDisabled}
      />
    );
  };

  const mappedCategories = [
    { id: 'all', name: 'All Tasks', icon: require('../../assets/icons/all-tasks.png') },
    { id: 'personal', name: 'Personal', icon: require('../../assets/icons/personal.png') },
    { id: 'medical', name: 'Medical', icon: require('../../assets/icons/medical.png') },
    { id: 'finance', name: 'Finance', icon: require('../../assets/icons/financial.png') },
    { id: 'property', name: 'Property', icon: require('../../assets/icons/property.png') },
    { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png') },
    { id: 'healthcareDirectives', name: 'Healthcare Directives', icon: require('../../assets/icons/healthcare.png') },
    { id: 'estatePlanning', name: 'Estate Planning', icon: require('../../assets/icons/estate-planning.png') },
    { id: 'carePreferences', name: 'Care Preferences', icon: require('../../assets/icons/final-care.png') },
    { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png') },
    { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png') },
    { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png') },
    { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png') },
    { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png') },
  ].map(cat => {
    if (cat.id === 'all') {
      // For all category, calculate total completion across all categories
      const totalAllTasks = PERSONAL_TASKS_CONFIG.length + MEDICAL_TASKS_CONFIG.length + FINANCE_TASKS_CONFIG.length +
                           PROPERTY_TASKS_CONFIG.length + FINAL_WISHES_TASKS_CONFIG.length +
                           HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length + ESTATE_PLANNING_TASKS_CONFIG.length + CARE_PREFERENCES_TASKS_CONFIG.length +
                           PASSWORDS_TASKS_CONFIG.length + CONTACTS_TASKS_CONFIG.length + EXPENSES_TASKS_CONFIG.length +
                           LOYALTY_POINTS_TASKS_CONFIG.length + 8; // 8 document categories
      // For finance, use intersection logic
      const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinanceTasksCount = completedTasks.filter(id => financeIds.has(Number(id))).length;

      // For property, use intersection logic
      const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
      const completedPropertyTasksCount = completedTasks.filter(id => propertyIds.has(Number(id))).length;

      // For final wishes, use intersection logic
      const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinalWishesTasksCount = completedTasks.filter(id => finalWishesIds.has(Number(id))).length;

      // For healthcare directives, use intersection logic
      const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedHealthcareDirectivesTasksCount = completedTasks.filter(id => healthcareDirectivesIds.has(Number(id))).length;

      // For estate planning, use intersection logic
      const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
      const completedEstatePlanningTasksCount = completedTasks.filter(id => estatePlanningIds.has(Number(id))).length;

      // For care preferences, use intersection logic
      const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCarePreferencesTasksCount = completedTasks.filter(id => carePreferencesIds.has(Number(id))).length;

      const completedAllTasks = completedTasks.length + completedMedicalTasks.length + completedFinanceTasksCount +
                               completedPropertyTasksCount + completedFinalWishesTasksCount +
                               completedHealthcareDirectivesTasksCount + completedEstatePlanningTasksCount + completedCarePreferencesTasksCount +
                               completedPasswordTasks.length + completedContactTasks.length + completedExpenseTasks.length +
                               completedLoyaltyPointTasks.length + completedDocumentTasks.length;
      return { ...cat, count: completedAllTasks, totalTasks: totalAllTasks };
    } else if (cat.id === 'personal') {
      // For personal category, compute from completedTasks intersecting PERSONAL_TASKS_CONFIG
      const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedPersonalTasksCount = completedTasks.filter(id => personalIds.has(Number(id))).length;
      const totalPersonalTasks = PERSONAL_TASKS_CONFIG.length;
      return { ...cat, count: completedPersonalTasksCount, totalTasks: totalPersonalTasks };
    } else if (cat.id === 'medical') {
      // For medical category, compute from completedTasks intersecting MEDICAL_TASKS_CONFIG
      const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
      const completedMedicalTasksCount = completedTasks.filter(id => medicalIds.has(Number(id))).length;
      const totalMedicalTasks = MEDICAL_TASKS_CONFIG.length;
      return { ...cat, count: completedMedicalTasksCount, totalTasks: totalMedicalTasks };
    } else if (cat.id === 'finance') {
      // For finance category, compute from completedTasks intersecting FINANCE_TASKS_CONFIG (same as PropertyChecklistScreen)
      const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinanceTasksCount = completedTasks.filter(id => financeIds.has(Number(id))).length;
      const totalFinanceTasks = FINANCE_TASKS_CONFIG.length;
      return { ...cat, count: completedFinanceTasksCount, totalTasks: totalFinanceTasks };
    } else if (cat.id === 'property') {
      // For property category, compute from completedTasks intersecting PROPERTY_TASKS_CONFIG (same as PropertyChecklistScreen)
      const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
      const completedPropertyTasksCount = completedTasks.filter(id => propertyIds.has(Number(id))).length;
      const totalPropertyTasks = PROPERTY_TASKS_CONFIG.length;
      return { ...cat, count: completedPropertyTasksCount, totalTasks: totalPropertyTasks };
    } else if (cat.id === 'finalWishes') {
      // Mirror PropertyChecklistScreen: use intersection of completedTasks with FINAL_WISHES_TASKS_CONFIG
      const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedFinalWishesTasksCount = completedTasks.filter(id => finalWishesIds.has(Number(id))).length;
      const totalFinalWishesTasks = FINAL_WISHES_TASKS_CONFIG.length;
      return { ...cat, count: completedFinalWishesTasksCount, totalTasks: totalFinalWishesTasks };
    } else if (cat.id === 'healthcareDirectives') {
      // Use intersection of completedTasks with HEALTHCARE_DIRECTIVES_TASKS_CONFIG
      const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedHealthcareDirectivesTasksCount = completedTasks.filter(id => healthcareDirectivesIds.has(Number(id))).length;
      const totalHealthcareDirectivesTasks = HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length;
      return { ...cat, count: completedHealthcareDirectivesTasksCount, totalTasks: totalHealthcareDirectivesTasks };
    } else if (cat.id === 'estatePlanning') {
      // Use intersection of completedTasks with ESTATE_PLANNING_TASKS_CONFIG
      const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
      const completedEstatePlanningTasksCount = completedTasks.filter(id => estatePlanningIds.has(Number(id))).length;
      const totalEstatePlanningTasks = ESTATE_PLANNING_TASKS_CONFIG.length;
      return { ...cat, count: completedEstatePlanningTasksCount, totalTasks: totalEstatePlanningTasks };
    } else if (cat.id === 'carePreferences') {
      // Use intersection of completedTasks with CARE_PREFERENCES_TASKS_CONFIG
      const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));
      const completedCarePreferencesTasksCount = completedTasks.filter(id => carePreferencesIds.has(Number(id))).length;
      const totalCarePreferencesTasks = CARE_PREFERENCES_TASKS_CONFIG.length;
      return { ...cat, count: completedCarePreferencesTasksCount, totalTasks: totalCarePreferencesTasks };
    } else if (cat.id === 'passwords') {
      // For passwords category, use password data to calculate progress
      const completedPasswordTasksCount = completedPasswordTasks.length;
      const totalPasswordTasks = PASSWORDS_TASKS_CONFIG.length;
      return { ...cat, count: completedPasswordTasksCount, totalTasks: totalPasswordTasks };
    } else if (cat.id === 'contacts') {
      // For contacts category, use contact data to calculate progress
      const completedContactTasksCount = completedContactTasks.length;
      const totalContactTasks = CONTACTS_TASKS_CONFIG.length;
      return { ...cat, count: completedContactTasksCount, totalTasks: totalContactTasks };
    } else if (cat.id === 'expenses') {
      // For expenses category, use expense data to calculate progress
      const completedExpenseTasksCount = completedExpenseTasks.length;
      const totalExpenseTasks = EXPENSES_TASKS_CONFIG.length;
      return { ...cat, count: completedExpenseTasksCount, totalTasks: totalExpenseTasks };
    } else if (cat.id === 'loyaltyPoints') {
      // For loyalty points category, use loyalty point data to calculate progress
      const completedLoyaltyPointTasksCount = completedLoyaltyPointTasks.length;
      const totalLoyaltyPointTasks = LOYALTY_POINTS_TASKS_CONFIG.length;
      return { ...cat, count: completedLoyaltyPointTasksCount, totalTasks: totalLoyaltyPointTasks };
    } else if (cat.id === 'documents') {
      // For documents category, use document data to calculate progress
      const completedDocumentTasksCount = completedDocumentTasks.length;
      const totalDocumentTasks = 8; // 8 document categories: personal, legal, medical, financial, property, insurance, digital, other
      return { ...cat, count: completedDocumentTasksCount, totalTasks: totalDocumentTasks };
    } else {
      // For other categories, use backend progress
      const progress = categoryProgress.find(p => p.categoryId === cat.id) || { completedCount: 0, totalCount: 0 };
      return { ...cat, count: progress.completedCount, totalTasks: progress.totalCount };
    }
  });

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Loading checklist...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <View style={styles.headerPlaceholder} />
        </View>

        {isViewingOthersData && viewingUserName && (
          <View style={styles.viewingBanner}>
            <Text style={styles.viewingBannerText}>
              Viewing {viewingUserName}'s Data (View Only)
            </Text>
          </View>
        )}

        {/* Show upgrade banner for users without active subscription (only when viewing own data) */}
        {!isViewingOthersData && <UpgradeBanner variant="compact" />}

        <ChecklistCardScroll
          categories={mappedCategories}
          selectedCategory="finalCare"
          navigation={navigation}
          onCategoryPress={() => {}}
        />
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Final Care Checklist</Text>
        </View>
        <ScrollView 
          style={styles.checklistContainer} 
          contentContainerStyle={styles.checklistContentContainer}
          showsVerticalScrollIndicator={false}
        >
          {FINAL_CARE_TASKS_CONFIG.map(renderTaskItem)}
        </ScrollView>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: ms(125, 0.3),
    height: ms(125, 0.3),
  },
  headerPlaceholder: {
    width: s(30),
    height: s(30),
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(10),
    marginHorizontal: ms(15),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
  },
  checklistContainer: {
    flex: 1,
    paddingHorizontal: ms(15),
  },
  checklistContentContainer: {
    paddingTop: ms(10),
    paddingBottom: ms(150), // Increased padding to ensure the last task is visible above BottomNavBar
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: ms(10),
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  viewingBanner: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(10),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(15),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewingBannerText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
}); 